import { useRef, useState } from "react";
import { Camera } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";

const MAX_BYTES = 5 * 1024 * 1024;
const IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

async function squareImageFile(file) {
  if (!IMAGE_TYPES.has(file.type))
    throw new Error("Choose a JPG, PNG, or WebP image");
  if (file.size > MAX_BYTES) throw new Error("Photos must be 5 MB or smaller");
  const image = await createImageBitmap(file);
  const side = Math.min(image.width, image.height);
  const sourceX = Math.floor((image.width - side) / 2);
  const sourceY = Math.floor((image.height - side) / 2);
  const canvas = document.createElement("canvas");
  canvas.width = 640;
  canvas.height = 640;
  const context = canvas.getContext("2d");
  context.drawImage(image, sourceX, sourceY, side, side, 0, 0, 640, 640);
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.86),
  );
  if (!blob) throw new Error("The image could not be prepared");
  return new File(
    [blob],
    `${file.name.replace(/\.[^.]+$/, "") || "profile"}.jpg`,
    {
      type: "image/jpeg",
    },
  );
}

export default function ProfilePhotoUploader({ employeeId, onSaved }) {
  const input = useRef(null);
  const [uploading, setUploading] = useState(false);
  const upload = async (event) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected) return;
    setUploading(true);
    try {
      const cropped = await squareImageFile(selected);
      const data = new FormData();
      data.append("file", cropped);
      data.append("record_type", "employee");
      data.append("record_id", employeeId);
      data.append("category", "photo");
      data.append("sensitivity", "standard");
      const { data: uploaded } = await api.post("/files/upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data: profile } = await api.put(
        `/hrms/employees/${employeeId}/profile-photo`,
        {
          file_id: uploaded.id,
        },
      );
      toast.success("Profile photo updated");
      onSaved(profile.profile_photo_id);
    } catch (error) {
      toast.error(error.message || apiErr(error));
    } finally {
      setUploading(false);
    }
  };
  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={upload}
        data-testid="employee-profile-photo-input"
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={uploading}
        onClick={() => input.current?.click()}
        data-testid="employee-profile-photo-upload-button"
      >
        <Camera className="mr-1.5 h-4 w-4" />
        {uploading ? "Uploading…" : "Update photo"}
      </Button>
    </>
  );
}
